import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { NativeInvoicePayStatus } from "@common/schemas/domains/invoice/native-invoice-pay.schema";
import { useNativeInvoicePayQuery } from "@api/nativeInvoicePay.api";
import styles from "./PayInvoice.module.scss";

const STATUS_MESSAGES: Record<NativeInvoicePayStatus, string> = {
    [NativeInvoicePayStatus.PAYABLE]: "Redirecting you to secure checkout…",
    [NativeInvoicePayStatus.PAID]:
        "This invoice has already been paid. Thank you!",
    [NativeInvoicePayStatus.PROCESSING]:
        "Your payment is being processed. You can close this page.",
    [NativeInvoicePayStatus.VOID]: "This invoice is no longer valid.",
    [NativeInvoicePayStatus.UNCOLLECTABLE]:
        "This invoice is no longer collectible.",
    [NativeInvoicePayStatus.COLLECT_OFF]:
        "Online payment is not available for this invoice.",
    [NativeInvoicePayStatus.STRIPE_NOT_READY]:
        "The recipient is still setting up online payments. Please try again later.",
    [NativeInvoicePayStatus.AMOUNT_DUE_ZERO]:
        "There is nothing left to pay on this invoice.",
};

function formatAmount(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency.toUpperCase(),
        }).format(amount);
    } catch {
        return `${amount} ${currency.toUpperCase()}`;
    }
}

// Public, unauthenticated pay page reached via {FRONTEND_URL}/pay/{paymentToken}.
// The backend returns either a hosted Checkout URL (redirect) or a status the
// page renders. checkout.stripe.com never appears in emails/PDFs — only this
// stable Kick URL does.
export const PayInvoice: React.FC = () => {
    const { paymentToken } = useParams<{ paymentToken: string }>();
    const { data, isLoading, isError } = useNativeInvoicePayQuery(
        paymentToken ?? "",
    );

    const checkoutUrl =
        data?.found && data.page?.status === NativeInvoicePayStatus.PAYABLE
            ? data.page.checkoutUrl
            : null;

    useEffect(() => {
        if (checkoutUrl) {
            window.location.assign(checkoutUrl);
        }
    }, [checkoutUrl]);

    if (isLoading) {
        return (
            <PayInvoiceShell>
                <p className={styles.message}>Loading…</p>
            </PayInvoiceShell>
        );
    }

    if (isError) {
        return (
            <PayInvoiceShell>
                <h1 className={styles.title}>Something went wrong</h1>
                <p className={styles.message}>
                    We couldn't load this payment link right now. Please refresh
                    the page or try again in a few minutes.
                </p>
            </PayInvoiceShell>
        );
    }

    if (!data?.found || !data.page) {
        return (
            <PayInvoiceShell>
                <h1 className={styles.title}>Invoice not found</h1>
                <p className={styles.message}>
                    This payment link is invalid or has expired.
                </p>
            </PayInvoiceShell>
        );
    }

    const { status, invoiceNumber, entityName, amountDue, currency } =
        data.page;

    return (
        <PayInvoiceShell>
            {entityName ? <h2 className={styles.title}>{entityName}</h2> : null}
            {invoiceNumber ? (
                <p className={styles.invoiceNumber}>Invoice {invoiceNumber}</p>
            ) : null}
            <p className={styles.amount}>{formatAmount(amountDue, currency)}</p>
            <p className={styles.message}>{STATUS_MESSAGES[status]}</p>
            {status === NativeInvoicePayStatus.PAYABLE && checkoutUrl ? (
                <a className={styles.checkoutLink} href={checkoutUrl}>
                    Continue to payment
                </a>
            ) : null}
        </PayInvoiceShell>
    );
};

const PayInvoiceShell: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => (
    <div className={styles.page}>
        <div className={styles.card}>{children}</div>
    </div>
);
